import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import {
    zeroDateDetailsResponseSchema,
    updateZeroDateResponseSchema,
    ZeroDateDetailsRequest,
    ZeroDateDetailsResponse,
    UpdateZeroDateRequest,
    UpdateZeroDateResponse,
} from './projectLocation.types';

export type {
    ZeroDateDetailsRequest,
    ZeroDateDetailsResponse,
    ZeroDateDetailsEpicDate,
    UpdateZeroDateRequest,
    UpdateZeroDateResponse,
    UpdateZeroDateEpicDate,
} from './projectLocation.types';
export { DateShiftOption } from './projectLocation.types';

const ZERO_DATE_DETAILS_QUERY_KEY = 'ProjectLocationZeroDateDetails';

// ---- Zero Date Details ----

const getZeroDateDetails = async (request: ZeroDateDetailsRequest): Promise<ZeroDateDetailsResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}projectlocation/zero-date-details`, { json: request }).json();
    return zeroDateDetailsResponseSchema.parse(response);
};

export const useGetZeroDateDetails = (
    request: ZeroDateDetailsRequest,
    options?: { enabled?: boolean }
): UseQueryResult<ZeroDateDetailsResponse> => {
    return useQuery<ZeroDateDetailsResponse>({
        queryKey: [ZERO_DATE_DETAILS_QUERY_KEY, request],
        queryFn: () => getZeroDateDetails(request),
        enabled: options?.enabled !== undefined ? options.enabled : true,
    });
};

// ---- Update Zero Date ----

const updateZeroDate = async (request: UpdateZeroDateRequest): Promise<UpdateZeroDateResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}projectlocation/update-zero-date`, { json: request }).json();

    const parsed = updateZeroDateResponseSchema.safeParse(response);
    if (!parsed.success) {
        throw new Error((response as any)?.message ?? 'An unexpected error occurred');
    }
    if (parsed.data.isError) {
        throw new Error(parsed.data.errors?.[0]?.message ?? 'An unexpected error occurred');
    }
    return parsed.data;
};

// NOTE: pacerApi only shows its own error notification once the retries are exhausted, so a
// non-retryable status (400, 403, 500...) never produces one. Callers must surface onError
// themselves or the failure is silent.
export const useUpdateZeroDate = (
    callback?: (results: UpdateZeroDateResponse) => void,
    onError?: (error: Error) => void
): UseMutationResult<UpdateZeroDateResponse, Error, UpdateZeroDateRequest> => {
    return useMutation<UpdateZeroDateResponse, Error, UpdateZeroDateRequest>({
        mutationFn: updateZeroDate,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
        },
        onError: (error) => {
            onError?.(error);
        },
    });
};
